/**
 * TOSS Web Application — Client Portal wire types (releases Р1 + Р2 + Р4)
 *
 * Single source of the `/api/client/*` JSON shapes. Transcribed field-for-field from the
 * fixed contracts:
 *   - vault/specs/client-portal/prep-work/r1-api-contract.md          §2, §5, §8
 *   - vault/specs/client-portal/prep-work/r1-client-dtos.md           §3, §4
 *   - vault/specs/client-portal/prep-work/r1-contract-review-decisions.md (OVERRIDES the two above)
 *   - vault/specs/client-portal/prep-work/r2-api-contract.md          §1, §2, §5 (documents, dashboard, colleagues)
 *   - vault/specs/client-portal/prep-work/r2-contract-decisions.md    (OVERRIDES r2-api-contract)
 *   - vault/specs/client-portal/prep-work/r4-approval-is-a-frozen-proposal-answered-in-the-chat.md §4-§5
 *   - vault/specs/client-portal/prep-work/r4-approval-card-notifications-and-screens.md §10 (chat card)
 *   - vault/specs/client-portal/prep-work/r4-contract-decisions.md    (OVERRIDES both r4 notes — B3/B4/B5)
 *   - vault/specs/client-portal/prep-work/r4-fleet-is-scoped-to-our-own-service-history.md §2-§6 (containers, ПТО)
 *
 * Nothing is added and nothing is trimmed relative to that contract. In particular:
 *   - the client vocabulary of statuses is its OWN five values, never the internal
 *     `ApplicationStatus` (see {@link ClientApplicationStatus});
 *   - `createdAtLocal` / `timezoneId` are deliberately absent — the server sends UTC only and
 *     the portal renders in the browser timezone (contract §8);
 *   - the message card `payload` is an OBJECT, not a JSON string (contract §3.4 + §5);
 *   - no client DTO carries a mechanic, a price, a rate or a payment field, WITH ONE SCOPED
 *     exception since Р4: {@link ClientApprovalDto} / {@link ClientApprovalLineDto} carry this
 *     customer's own `costClient` for a service being OFFERED — decision B4. Every other DTO,
 *     including the approval's own chat-card payload and dashboard summary, stays money-free.
 *     The full blacklist is pinned server-side by `ClientDtoExposureTests` (r1-client-dtos §4).
 *
 * These types live OUTSIDE `features/client/**` on purpose: the ESLint boundary (decision Р6)
 * makes a static import of `features/client/**` from anywhere else an error, so shared layout
 * chrome could never reach a DTO defined in there. `api/clientPortal/` (not `api/client/`)
 * because `src/api/client.ts` — the axios instance — already owns the `client` specifier and
 * the boundary globs cannot tell the two apart.
 *
 * There is deliberately NO barrel `index.ts` in this folder: every consumer imports the explicit
 * module path, so no later step has to edit a file another step owns.
 */

import type { PaginatedResponse } from '@/types/api.types';

// ============================================================================
// Base path
// ============================================================================

/**
 * Root of the external customer API space. The portal never calls `/api/lk/*` or
 * `/api/mobile/*` — a client JWT is rejected by those policies (phase0-part2 §1).
 */
export const CLIENT_API_BASE = '/api/client';

// ============================================================================
// Vocabularies (string unions — the backend serialises every enum as a string)
// ============================================================================

/**
 * The client's OWN status vocabulary — five values, mapped from the internal
 * `ApplicationStatus` by `ClientApplicationStatusMap.ToClient` (contract §1):
 *
 * | internal                      | client      | statusLabel        |
 * |-------------------------------|-------------|--------------------|
 * | `New`                         | `New`       | Новая              |
 * | `WorkMechanic`, `WorkFmo`     | `InWork`    | В работе           |
 * | `WorkAccountant`              | `WorkDone`  | Работы выполнены   |
 * | `Completed`, `Archive`        | `Closed`    | Закрыта            |
 * | `Cancel`                      | `Cancelled` | Отменена           |
 *
 * Do NOT reuse `ApplicationStatus` from `@/types/enums` for portal screens: the customer must
 * never see `WorkMechanic` / `WorkFmo` / `WorkAccountant`, which is exactly what that enum
 * exposes. Every payload that carries a status also carries a human `statusLabel`, so the
 * portal never derives Russian text for it — render `statusLabel`, switch on `status`.
 */
export type ClientApplicationStatus = 'New' | 'InWork' | 'WorkDone' | 'Closed' | 'Cancelled';

/** Application priority. Same four values as the internal enum — the visibility matrix allows it. */
export type ClientApplicationPriority =
  | 'WithoutPriority'
  | 'Medium'
  | 'High'
  | 'EmergencyRepair';

/** ПТО inspection progress. Always paired with a server-rendered `statusLabel`. */
export type ClientPtoStatus =
  | 'NotExamined'
  | 'NotCompleted'
  | 'PartiallyCompleted'
  | 'Completed';

/** What an inspected line of a ПТО is. */
export type ClientApplicationItemType = 'Container' | 'Equipment';

/**
 * History actions the portal is allowed to see. The server filters the audit log down to this
 * whitelist (r1-client-dtos §3.3): `Assign` (mechanic assignment), `Verify` and `View` are
 * dropped, because "назначен механик Иванов" would walk straight past decision Р17.
 *
 * The four `AdditionalWork*` values arrived with release Р4 and this type did not follow until
 * 2026-08-21 — so «Доп. работы согласованы» rendered through the `default` branch of
 * `ClientApplicationHistory`, as the same grey pencil that means «изменено». The server
 * dispatches them on the PAIR (entityType, action), which is why an approval reusing `Create`
 * does not print «Заявка создана».
 */
export type ClientHistoryAction =
  | 'Created'
  | 'StatusChanged'
  | 'Completed'
  | 'Cancelled'
  | 'Updated'
  | 'AdditionalWorkRequested'
  | 'AdditionalWorkApproved'
  | 'AdditionalWorkRejected'
  | 'AdditionalWorkWithdrawn'
  /** This application was merged into another one — «Объединена с заявкой N1» (application-merge). */
  | 'MergedInto'
  /** Other applications were merged into this one — «В заявку влиты N2, N3» (application-merge). */
  | 'MergedFrom';

/**
 * Which side of the company chat a message came from. Derived server-side from
 * `UserAudience.IsExternal(sender.Role)` — the portal must not infer it from names.
 */
export type ClientChatSenderSide = 'Toss' | 'Client';

/** Message kind. `System` and `Card` are minted by the server only. */
export type ClientChatMessageType = 'Text' | 'File' | 'System' | 'Card';

/**
 * Notification bucket. `Task` and `Expense` notifications are collapsed into `Other` on the
 * way out (review decision Р5) — the portal never learns internal notification types.
 */
export type ClientNotificationType = 'Application' | 'Message' | 'Other';

// ============================================================================
// Applications, ПТО, inspections
// ============================================================================

/**
 * One row of `GET /api/client/applications`.
 *
 * The counters here come from stored scalars on the application (review decision "Счётчики в
 * списке заявок"): the list endpoint is a SQL projection, so nothing that needs materialised
 * entities can appear on it.
 */
export interface ClientApplicationListDto {
  id: string;
  applicationNumber: string;
  status: ClientApplicationStatus;
  /** Server-rendered Russian label for {@link status}. Render as-is; never re-map. */
  statusLabel: string;
  priority: ClientApplicationPriority;
  /** ISO-8601 **UTC**. Render in the browser timezone (contract §8). */
  createdAt: string;
  /**
   * «Дата выполнения» — момент закрытия последнего ПТО заявки. ISO-8601 **UTC**, рендерится в
   * поясе браузера, как и {@link createdAt}.
   *
   * `null`, пока хоть одно ПТО открыто, и у заявки без ПТО. Это НЕ дата статуса «Закрыта»: тот
   * следует за оплатой, а не за работой механика. Считается сервером из ПТО, в базе не хранится.
   */
  workCompletedAt: string | null;
  /** `DirectionsMovement.DirectionName` of the picked direction. */
  directionName: string | null;
  dislocation: string | null;
  typeCargo: string | null;
  shipperName: string | null;
  consigneeName: string | null;
  /** The customer's own reference number for the application. Not unique. */
  externalId: string | null;
  containersTotalCount: number;
  containersInspectedCount: number;
  equipmentTotalCount: number;
  equipmentInspectedCount: number;
  /**
   * Numbers of those power units, in route order (31.08.2026) — the readable half of
   * {@link equipmentTotalCount}, and what the card prints instead of it.
   *
   * Containers have no counterpart here on purpose: they are declared as a QUANTITY and usually
   * carry no per-unit row at all, while a power unit is always an entity with a number (ADR-034).
   * That asymmetry is the rule the card follows — ящики числом, установки номерами.
   */
  equipmentNumbers: string[];
  /**
   * «Энергоустановки» одной строкой: на каждую единицу пара «тип номер» («ДГК 233ОЛРПР»),
   * единицы через запятую. Значение колонки таблицы и её ячейки в выгрузке Excel.
   *
   * Скаляр, а не массив, — ровно ради выгрузки: `defaultFormatter` печатает `row[column.key]`,
   * и лист обязан прочитаться так же, как экран. Те же единицы, что в {@link equipmentNumbers},
   * в том же маршрутном порядке: сервер собирает оба списка одним проходом.
   */
  equipmentUnits: string;
  /**
   * «Контейнеры» одной строкой: на каждый ящик «тип размер номер»
   * («Стандартный 20DC MSCU1000137»), ящики через запятую.
   *
   * ПУСТО, когда контейнеры объявлены количеством и своих строк состава не имеют (ADR-034 —
   * обычный для кабинета случай). Поэтому счётчик {@link containersTotalCount} остаётся
   * отдельной колонкой, а не уступает место этой.
   */
  containerUnits: string;
  ptoCount: number;
  /**
   * Names of the service points on the route, in route order (27.08.2026).
   *
   * The readable half of {@link ptoCount}, added so «Заявки этого ПТО» has a column to land on.
   * The column is off by default and the table force-shows it while a filter stands on it, so a
   * customer who never follows that link sees no change. The FILTER underneath carries directory
   * ids, not these names — the same split «Направление» makes.
   */
  ptoNames: string[];
  /** ПТО whose status is not `NotExamined`. */
  ptoInspectedCount: number;
  isCancelled: boolean;
  cancelReason: string | null;
  /**
   * Release Р6 (contract §4.4) — server-rendered, non-null ONLY when the application came
   * through the e-mail gateway (`ApplicationSource.Email`). `null` for every other source,
   * including a portal-created or dispatcher-created application — the customer has no use
   * for «создана диспетчером», and telling portal apart from staff would be a disclosure
   * nobody asked for. Render as-is, next to the status badge; never re-derive from an enum
   * the portal does not receive.
   */
  sourceLabel: string | null;
  /**
   * The application this one was merged into (application-merge spec); `null` for every other
   * application. A merged source keeps `status: 'Cancelled'` and `isCancelled` — the board's
   * «Отменена» column — while {@link statusLabel} reads «Объединена с N1».
   */
  mergedInto: ClientMergedInto | null;
}

/**
 * The target of a merged source — `mergedInto` on the list and the card (application-merge
 * spec). The portal's own mirror of the wire shape, like every type in this module.
 */
export interface ClientMergedInto {
  /** The target's id — the card the banner opens. */
  id: string;
  applicationNumber: string;
}

/**
 * `details` of an `APPLICATION_MERGED` refusal (400 on the portal's edit, cancel and upload): the
 * application the customer tried to write was merged into this one.
 */
export interface ClientApplicationMergedDetails {
  mergedIntoApplicationId: string;
  mergedIntoApplicationNumber: string;
}

/**
 * Server-side video compression state (spec «video is accepted wherever work photos are, and
 * compressed to 720p on the server»). Own vocabulary, not imported from `types/api.types` — this
 * module mirrors every shared enum locally (see {@link ClientApplicationStatus}), never imports
 * one, so a portal-only build never depends on the internal wire shapes.
 */
export type ClientFileProcessingStatus = 'None' | 'Pending' | 'Processing' | 'Ready' | 'Failed';

/** A file the portal is allowed to open, as referenced from an application. */
export interface ClientFileRefDto {
  id: string;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  /** Video release. `null` only when the id has no `StoredFiles` row at all (legacy reference). */
  processingStatus?: ClientFileProcessingStatus | null;
  /** Video release. Seconds, from ffprobe; `null` for an image/PDF or before processing runs. */
  durationSeconds?: number | null;
}

/**
 * Response of `GET /api/client/files/{id}/playback` (video release) — the only way the cabinet
 * ever gets a playable video URL. `url`/`posterUrl` are short-lived signed ADR-040 URLs (same
 * family as `/api/files/{id}`), never a `blob:` fetch: decision Р10 is about not putting a JWT in
 * an address bar, not about signed URLs as such, and a compressed clip is still tens of megabytes
 * — a `blob:` download would have to arrive whole before the first frame plays.
 */
export interface ClientFilePlaybackDto {
  url: string;
  posterUrl: string | null;
  /** ISO-8601 UTC. When `url`/`posterUrl` stop being accepted. */
  expiresAt: string;
  processingStatus: ClientFileProcessingStatus;
  contentType: string;
  durationSeconds: number | null;
}

/** One inspected container or power unit inside a ПТО. */
export interface ClientApplicationItemDto {
  id: string;
  itemType: ClientApplicationItemType;
  containerNumber: string | null;
  equipmentNumber: string | null;
  /**
   * Адрес карточки ящика в «Моём парке» (`/client/containers/{id}`), где уже лежит история его
   * обслуживания у этой компании. Без него заказчик читал на заявке один визит и не имел с неё
   * пути к «а что с этим ящиком было раньше».
   */
  containerId: string | null;
  /**
   * Есть у установки, но вести по нему **некуда**: карточки энергоустановки в кабинете нет,
   * парк — только контейнеры. Плитка печатает номер без ссылки, а не выдумывает адрес.
   */
  equipmentId: string | null;
  /** Server-rendered Russian label (e.g. «ДГК»). Render as-is. */
  equipmentTypeLabel: string | null;
  /** `null` until the item has actually been inspected. */
  inspection: ClientInspectionResultDto | null;
}

/** Inspection outcome for a single item. Carries no mechanic and no money — by design. */
export interface ClientInspectionResultDto {
  /** ISO-8601 UTC. */
  inspectedAt: string;
  repairsNeeded: boolean;
  itemServiced: boolean;
  inspectionPossible: boolean;
  inspectionNotPossibleReason: string | null;
  repairPlatform: string | null;
  repairComment: string | null;
  inspectionComment: string | null;
  /**
   * File ids of inspection photos. Open them through `GET /api/client/files/{id}` — the raw
   * `/api/files/{id}` URL is an internal, anonymous route and must never be built here.
   */
  photoFileIds: string[];
  /**
   * The same photos as {@link photoFileIds}, resolved to server-built refs — real content type,
   * processing status, duration (video release). `photoFileIds` stays for one release so an older
   * client keeps working; always an array (never `null`), same as the server property.
   */
  photoFiles: ClientFileRefDto[];
}

/** One ПТО of an application, with its inspected items. */
export interface ClientApplicationPtoDto {
  id: string;
  order: number;
  ptoName: string;
  ptoDescription: string | null;
  status: ClientPtoStatus;
  /** Ожидает осмотра / Не выполнен / Выполнен частично / Осмотрен. Render as-is. */
  statusLabel: string;
  operationsRequest: string | null;
  containersTotalCount: number;
  containersInspectedCount: number;
  equipmentTotalCount: number;
  equipmentInspectedCount: number;
  /** Bulk inspection comment left by the crew (`ApplicationPTO.BulkInspectionComment`). */
  inspectionComment: string | null;
  /** ISO-8601 UTC, `null` while the ПТО is still open. */
  inspectionCompletedAt: string | null;
  /**
   * Id ПТО в справочнике — НЕ id этого назначения ({@link ClientApplicationPtoDto.id}).
   * Тот же id, что несут строки «ПТО, с которыми работаем».
   */
  ptoId: string;
  /** Названия станций этого ПТО. Только имена: километраж — основание для цены пробега. */
  stations: string[];
  /** Терминал, если визит был на терминале. */
  terminalName: string | null;
  /**
   * Выписан ли акт по этому ПТО. **Признак, и никогда номер** — `actNumber` в списке запрещённых
   * ключей; сам файл берётся по id ПТО через `GET /api/client/documents/act/{ptoId}`.
   */
  hasAct: boolean;
  /** Что сделали на этом ПТО: поштучные услуги и общие. Без денег — их негде хранить. */
  works: ClientPtoWorkDto[];
  /**
   * Дефекты по контейнерам, которые едут количеством. Отдельный список, а не часть `items`:
   * строки `ApplicationPTOItem` у них нет и быть не может.
   *
   * Только СТАРЫЙ путь записи (мигрированные строки и легаси-осмотр). Дефект, найденный через
   * новый флоу осмотра (inspection redesign), в этот список не попадает — читай {@link defects}.
   */
  containersNeedingRepair: ClientPtoRepairDto[];
  /**
   * Дефекты нового флоу осмотра, единым списком для контейнеров и энергоустановок (inspection
   * redesign, Phase 3) — решение диспетчера сюда никогда не попадает (decision 8:
   * `PTO_BLOCK_AUDIENCE.handovers` остаётся `customer: false`).
   *
   * Поле новое: точные значения `kind` не были подтверждены сверкой с бэкендом на момент правки
   * (контракт применялся параллельно) — рендер ниже не ветвится по `kind` и печатает `unitNumber`
   * независимо от него, поэтому неожиданное значение не ломает экран.
   */
  defects: ClientPtoDefectDto[];
  /** Фото массового осмотра. На ПТО, осмотренном «скопом», других снимков не существует. */
  bulkInspectionPhotoFileIds: string[];
  /**
   * The same bulk-inspection photos as {@link bulkInspectionPhotoFileIds}, resolved to
   * server-built refs (video release). {@link bulkInspectionPhotoFileIds} stays for one release;
   * always an array, never `null`.
   */
  bulkInspectionPhotoFiles: ClientFileRefDto[];
  items: ClientApplicationItemDto[];
}

/**
 * Дефект, найденный новым флоу осмотра — контейнер или энергоустановка, одной формой
 * (inspection redesign, Phase 3). Заказчик видит находку и (если её уже закрыли) на каком ПТО —
 * решение диспетчера сюда не входит.
 */
export interface ClientPtoDefectDto {
  unitNumber: string | null;
  /**
   * Directory id of the container or the power unit — `null` only for a hand-typed number in
   * neither fleet. Added 2026-09-03 (server-side fix for the duplicate this field's own sibling,
   * `ClientApplicationPtoDto.containersNeedingRepair`, caused): `defects[]` is a strict superset
   * of `containersNeedingRepair[]` for containers, so the two ALWAYS overlap, not only on migrated
   * data — dedupe on this against `containersNeedingRepair[].containerId`, not on the number (a
   * `UnitDefect` row does not denormalise `ContainerNumber`, so number-matching alone misses rows
   * the new flow itself produces).
   */
  unitId: string | null;
  /** Confirmed against the backend's own changelog note 2026-09-03 — a closed union, not a guess. */
  kind: 'Container' | 'Equipment';
  description: string | null;
  platform: string | null;
  /**
   * File ids, opened the same way as every other photo list on this surface
   * (`GET /api/client/files/{id}`). Named `photos`, not `photoFileIds` like its siblings on this
   * DTO family — verified against the live API 2026-09-03; an earlier pass guessed the sibling
   * convention and was wrong.
   */
  photos: string[];
  /**
   * {@link photos} resolved to server-built refs (video release). Optional and nullable — the
   * server sends it, but the record type defaults it to `null`, unlike this DTO family's other
   * `…Files` siblings, which default to an empty array.
   */
  photoFiles: ClientFileRefDto[] | null;
  /** Имя ПТО, где дефект устранили, — «устранено на ПТО …». `null` = ещё не устранён. */
  resolvedAtPtoName: string | null;
}

/**
 * Дефект, записанный бригадой против контейнера, который едет количеством.
 *
 * `containerId` пуст у строки, набранной руками (форма снимает локальный `temp-` id при отправке),
 * и у номера, которого нет в справочнике. Номер печатается в любом случае, ссылка ставится только
 * при наличии id: дефект без карточки — всё ещё дефект, который заказчик обязан прочитать.
 */
export interface ClientPtoRepairDto {
  containerId: string | null;
  containerNumber: string | null;
  platform: string | null;
  comment: string | null;
  /** Открывать через `GET /api/client/files/{id}`, как и остальные снимки осмотра. */
  photoFileIds: string[];
  /** {@link photoFileIds} resolved to server-built refs (video release). See `ClientPtoDefectDto.photoFiles` for the nullability note. */
  photoFiles: ClientFileRefDto[] | null;
}

/** Response of `GET /api/client/applications/{id}` and of `POST /api/client/applications`. */
export interface ClientApplicationFullDto extends ClientApplicationListDto {
  comment: string | null;
  /** ПЛМ (platform / wagon numbers) attached to the application. */
  plmValues: string[];
  attachedFiles: ClientFileRefDto[];
  /** Ordered by `order`. Empty until a dispatcher accepts the application and adds ПТО. */
  ptos: ClientApplicationPtoDto[];
  /**
   * Id of the picked movement direction — NEW in release Р5
   * (r5-copy-and-list-views.md §1.1). `directionName` alone cannot drive the create form's
   * direction `<Select>` on «Копировать заявку»: matching by display string silently loses
   * the direction whenever a name is edited in the directory. The id is shared reference
   * data (the same ids `GET /api/client/directions` already returns), so exposing it
   * discloses nothing new. Absent from {@link ClientApplicationListDto} on purpose — copying
   * from the list navigates to the create page, which loads the full card anyway.
   */
  directionsMovementId: string | null;
  /**
   * Tracking links stored on the application — the customer's own, or ones a dispatcher added
   * afterwards (release Р34 add-on).
   *
   * Shipped because the create form now asks for them: a field one can fill in and never see again
   * reads as a lost answer. Always an array, empty when there are none.
   */
  trackingUrls: string[];
  /**
   * The composition the edit form sends back — added 31.08.2026 with
   * `PUT /api/client/applications/{id}`.
   *
   * A PUT is a full replace, so the card has to be able to STATE what the application holds:
   * a form filled from a card that cannot name its containers would save that choice away on
   * the first «Сохранить». This is the same contract the templates already carry — the GET
   * returns every field the PUT accepts.
   *
   * Ids of the caller's own fleet (the ones `GET /api/client/containers` lists), ordered by
   * number rather than by the order they were picked: the schema stores no order for items.
   */
  containerIds: string[];
  equipmentItems: ClientApplicationEquipmentItemDto[];
  servicePoints: ClientApplicationServicePointDto[];
  /**
   * Wagon numbers with NO unit behind them — exactly what `plmItems` of a create or an update
   * carries, and deliberately a second list next to {@link plmValues}.
   *
   * `plmValues` is everything the card prints, loose numbers and the numbers of chosen units
   * together. The write body wants only the loose half: the other half travels inside
   * {@link equipmentItems} and would otherwise be saved twice — once as a unit's wagon, once as
   * a stray number nobody typed.
   */
  loosePlmValues: string[];
}

/**
 * One energy unit of an application, as the edit form needs it back: the unit and the wagon it
 * travels on. `plmValue` is null for a unit sent without a number.
 */
export interface ClientApplicationEquipmentItemDto {
  equipmentId: string;
  plmValue: string | null;
}

/**
 * One service point of an application, as the edit form needs it back.
 *
 * `ptoId` is the DIRECTORY id — what the picker selects and what the PUT accepts — not the
 * `ApplicationPTO` row id that {@link ClientApplicationPtoDto.id} carries. The card block and this
 * block answer different questions and deliberately do not share a type.
 */
export interface ClientApplicationServicePointDto {
  ptoId: string;
  operationsRequest: string | null;
  comment: string | null;
}

/** One line of `GET /api/client/applications/{id}/history`. */
export interface ClientHistoryDto {
  id: string;
  /** ISO-8601 UTC. */
  timestamp: string;
  action: ClientHistoryAction;
  /**
   * Server-rendered Russian sentence («Заявка создана», «Статус изменён», …). The audit log's
   * own `Description` is never forwarded — it is written for staff.
   */
  title: string;
}

// ============================================================================
// Application templates (release Р5)
// ============================================================================

/**
 * One row of `GET /api/client/application-templates`.
 *
 * The heavy values are deliberately absent — `plmValues`, `dislocation` and `comment` live
 * on {@link ClientApplicationTemplateFullDto} only (50 templates ×
 * 100 wagon numbers × 500 chars would be a 2.5 MB payload for a screen that shows names).
 * `plmCount` is what this row carries instead.
 *
 * That absence is also why editing must hydrate before it replaces (blocker B1,
 * r5-contract-decisions.md §1): `PUT` is a full replace, so a rename issued from a list row
 * that never loaded these four fields would write four NULLs over a 40-wagon route sheet.
 * Обе двери в правку ждут `GET /{id}`: форма (`ClientApplicationCreatePage` в режиме шаблона —
 * тот же гейт монтирования, что у «Копировать заявку», и по той же причине: react-hook-form
 * читает `defaultValues` один раз) и лист переименования (`ClientRenameTemplateDialog`), где
 * дешевизна жеста особенно легко вводит в заблуждение.
 */
export interface ClientApplicationTemplateListDto {
  id: string;
  /** The customer's own name for the shortcut. Not unique inside a company by design. */
  name: string;
  /** Resolved at read time. `null` both when the template stores no direction and when the
   *  stored one has since been deleted — {@link ClientApplicationTemplateListDto.directionUnavailable}
   *  is what tells the two apart. */
  directionName: string | null;
  /** `true` only when an id IS stored but no longer resolves. On the ROW, not just the card:
   *  the list is where a customer picks a shortcut, and without it a template whose direction
   *  was deleted looks exactly like one that never had a direction — the difference used to
   *  surface only after the create form was already open and prefilled. */
  directionUnavailable: boolean;
  /** «От кого». */
  shipperName: string | null;
  /** «Кому». */
  consigneeName: string | null;
  typeCargo: string | null;
  containersTotalCount: number | null;
  /** How many wagon numbers the template holds — the numbers themselves are on the card. */
  plmCount: number;
  /** ФИО of the colleague who created the template, or `null` — never an email or a phone
   *  number, and resolved only for a portal account of this same company (contract §3). */
  createdByName: string | null;
  /** ISO-8601 UTC. */
  createdAt: string;
  /** ISO-8601 UTC, `null` until the first edit. */
  updatedAt: string | null;
}

/**
 * Response of `GET /api/client/application-templates/{id}`, and of `POST` / `PUT` — the list
 * row plus everything the create form's «Заполнить из шаблона» and the edit modal need.
 *
 * Keys that must never appear on either template type (by construction, server-enforced):
 * `customerId`, `createdById`, `externalId`, `sourceApplicationId`, `status`, `priority`, and
 * every money key. There is no `externalId` at all (contract §4.3) — a per-shipment document
 * number baked into a reusable shortcut would stamp every future application with the same
 * reference in the customer's own 1С / ОС РЖДЛ.
 */
export interface ClientApplicationTemplateFullDto extends ClientApplicationTemplateListDto {
  /** For the create form's `<Select>`. `null` both when the template stores no direction
   *  and when the stored id no longer resolves; which of the two happened is
   *  {@link ClientApplicationTemplateListDto.directionUnavailable}. The template still
   *  applies; saving it again repairs the stored id (contract §4.4). */
  directionsMovementId: string | null;
  dislocation: string | null;
  comment: string | null;
  /** Wagon / platform numbers, in the order the customer entered them. */
  plmValues: string[];
  /** Насколько срочной заказчик считает такую перевозку. `null` — «без приоритета». */
  priority: ClientApplicationPriority | null;

  /**
   * Ids контейнеров собственного парка — ровно те, что записаны, без отбраковки.
   *
   * Сервер здесь ничего не проверяет: контейнер могли продать за месяц между сохранением
   * заготовки и её открытием, и 404 на весь шаблон из-за этого был бы хуже. Сказать об этом
   * должен ЭКРАН, у которого парк компании и так загружен, а повторное сохранение чинит запись —
   * то же поведение, что у направления (`directionUnavailable`).
   */
  containerIds: string[];

  /** Энергоустановки с номером вагона у каждой. Читаются так же лениво. */
  equipmentItems: ClientApplicationTemplateEquipmentItem[];

  /** Пункты обслуживания, в порядке заказчика. Читаются так же лениво. */
  servicePoints: ClientApplicationTemplateServicePoint[];
}

/**
 * The nine prefill fields shared by the create and update bodies — identical shape to
 * `CreateClientApplicationRequest` minus `externalId` and `attachedFileIds` (contract §3:
 * every length mirrors that request exactly, so a template can never hold a value the create
 * endpoint would then refuse).
 */
export interface ClientApplicationTemplateFieldsRequest {
  /** Required, 1..200. */
  name: string;
  /** ≤ 300 */
  shipperName?: string;
  /** ≤ 300 */
  consigneeName?: string;
  /** Must exist, otherwise 404. Source: `GET /api/client/directions`. */
  directionsMovementId?: string;
  /** ≤ 500 */
  dislocation?: string;
  /** ≤ 200 */
  typeCargo?: string;
  /** ≤ 2000 */
  comment?: string;
  /** 0..10000 */
  containersTotalCount?: number;
  /** ≤ 100 items, each ≤ 500 chars. */
  plmValues?: string[];

  /**
   * Насколько срочной заказчик считает такую перевозку. Не задано — «без приоритета».
   *
   * Появилось 27.08.2026 вместе с парком и пунктами обслуживания. Их отсутствие было не решением:
   * сущность шаблона написана 19.08, когда заявка из кабинета состояла ровно из девяти полей, а
   * 25.08 заявка выросла и шаблон никто не догнал.
   */
  priority?: ClientApplicationPriority;

  /** Контейнеры собственного парка. Проверяются на активное владение при записи. */
  containerIds?: string[];

  /** Энергоустановки с номером вагона у каждой. */
  equipmentItems?: ClientApplicationTemplateEquipmentItem[];

  /** Пункты обслуживания, в порядке заказчика. Проверяется только существование ПТО. */
  servicePoints?: ClientApplicationTemplateServicePoint[];
}

/** Одна энергоустановка заготовки: чья она и на каком вагоне обычно едет. */
export interface ClientApplicationTemplateEquipmentItem {
  equipmentId: string;
  plmValue?: string | null;
}

/** Один пункт обслуживания заготовки. */
export interface ClientApplicationTemplateServicePoint {
  ptoId: string;
  operationsRequest?: string | null;
  comment?: string | null;
}

/**
 * Body of `POST /api/client/application-templates`. Two mutually exclusive shapes in one
 * type: `{ name, fromApplicationId }` («сохранить как шаблон» — the server copies the nine
 * fields off that application itself) or `{ name, ...fields }` (written from scratch on the
 * templates screen). Both at once is 400 `VALIDATION_ERROR`.
 *
 * What counts as "fields were given" is the predicate written out verbatim in
 * r5-contract-decisions.md §1 B3: any of the nine fields below is non-null and, for a
 * string, non-blank after `.trim()` — `''`, `'   '`, `containersTotalCount: 0` and
 * `plmValues: []` all count as ABSENT. `ClientSaveAsTemplateModal` mirrors this predicate
 * (dev-only assertion) at its one call site rather than exporting a second copy nothing
 * else imports.
 */
export interface CreateClientApplicationTemplateRequest extends ClientApplicationTemplateFieldsRequest {
  /** Application to copy the nine fields from. Mutually exclusive with every field above. */
  fromApplicationId?: string;
}

/**
 * Body of `PUT /api/client/application-templates/{id}` — a FULL replace, and there is no
 * `fromApplicationId` property to bind: a template is never re-pointed at an application.
 */
export type UpdateClientApplicationTemplateRequest = ClientApplicationTemplateFieldsRequest;

// ============================================================================
// Company
// ============================================================================

/** A contact person of the customer company. */
export interface ClientCompanyContactDto {
  id: string;
  contactName: string;
  phoneNumber: string;
  email: string | null;
  position: string | null;
}

/**
 * Response of `GET /api/client/company` — the read-only company card on the profile screen.
 * Commercial terms (`materialsMarkupPercent`, `paymentTerm`, …) are not part of it.
 */
export interface ClientCompanyDto {
  id: string;
  companyName: string;
  companyINN: string;
  bankAccount: string | null;
  contacts: ClientCompanyContactDto[];
}

// ============================================================================
// Files
// ============================================================================

/** Response of `POST /api/client/files`. */
export interface ClientFileUploadedDto {
  id: string;
  fileName: string;
  sizeBytes: number;
  contentType: string;
  /**
   * Server-side video compression state (video release). `'None'` for an image or PDF; a video
   * is playable from its source immediately and flips to `'Ready'` once the 720p copy lands —
   * see `GET /api/client/files/{id}/playback`.
   */
  processingStatus: ClientFileProcessingStatus;
}

// ============================================================================
// Chat
// ============================================================================

/** `kind` discriminator of a chat card payload. `invoice` (Р3) and `document` (Р6) are reserved. */
export const CHAT_CARD_KIND_APPLICATION = 'application';

/**
 * Payload of a `Card` message — an application posted into the company chat (contract §5).
 *
 * `kind` is open on purpose: an unknown kind must degrade to rendering `content` as plain text,
 * because both audiences read the same database row and the Flutter app does not know
 * `MessageType.Card` at all. `content` is therefore never empty.
 *
 * `status` / `statusLabel` are the CLIENT vocabulary even on the internal side — one row, two
 * audiences. `priority` is the raw internal enum value and is typed as `string` for exactly the
 * same reason `kind` is.
 */
export interface ChatCardPayload {
  kind: string;
  applicationId: string;
  applicationNumber: string;
  status: ClientApplicationStatus;
  statusLabel: string;
  priority: string;
  /** `DirectionName`, falling back to `Dislocation`; `null` when both are empty. */
  direction: string | null;
  containersCount: number;
}

/**
 * `kind` discriminator of the additional-work approval card — release Р4, decision B3.
 */
export const CHAT_CARD_KIND_ADDITIONAL_WORK = 'additionalWork';

/**
 * Payload of a `Card` message that offers additional work for approval — release Р4.
 *
 * **Carries no money and no lines, on purpose (decision B3).** Mechanics sit in every
 * company chat (Р17) and every `Mobile*Dto` in the project strips `CostClient` — a card
 * payload with a `lines[]`/`totalCostClient` would silently reverse that rule the moment
 * a mechanic's app rendered it, and a forwarded card would leak a customer's price to
 * whichever chat it landed in. The payload's only job is to let `ClientMessageCard` route
 * to {@link ClientApprovalCard} and pass an id; that component then reads the priced lines
 * from `GET /api/client/approvals/{requestId}`, a `ClientAccess`-only surface.
 *
 * `kind` is typed as `string`, matching {@link ChatCardPayload.kind} — see that field's own
 * comment for why.
 */
export interface AdditionalWorkCardPayload {
  kind: string;
  requestId: string;
  applicationId: string;
  applicationNumber: string;
  ptoName: string;
  lineCount: number;
}

/** `kind` discriminator of a work-event card — статусы, осмотры, состав заявки, документы. */
export const CHAT_CARD_KIND_EVENT = 'event';

/**
 * What happened. One card kind carries all of them, with the specific event here — ten kinds
 * would have meant ten branches in every renderer, and a renderer that forgets one silently
 * degrades the card to plain text.
 *
 * Typed as `string` at the payload, like every other discriminator in this file: an event this
 * build does not know must still render its `content`.
 */
export const CHAT_EVENT_KIND = {
  applicationAccepted: 'applicationAccepted',
  applicationWorkDone: 'applicationWorkDone',
  applicationClosed: 'applicationClosed',
  applicationCancelled: 'applicationCancelled',
  inspectionStarted: 'inspectionStarted',
  inspectionCompleted: 'inspectionCompleted',
  ptoAdded: 'ptoAdded',
  ptoRemoved: 'ptoRemoved',
  actReady: 'actReady',
  /** The customer changed an order still in «Новая»; the lines say what, never the values. */
  applicationUpdated: 'applicationUpdated',
  /**
   * A dispatcher merged other orders of the customer into this one (application-merge spec).
   * Posted on the TARGET, so the card's «Открыть заявку» opens the merged order on both surfaces.
   */
  applicationsMerged: 'applicationsMerged',
} as const;

/** Where the card's button leads. The server names the destination, never a URL. */
export const CHAT_EVENT_ACTION = {
  application: 'application',
  inspection: 'inspection',
  act: 'act',
} as const;

/**
 * Payload of a work-event card: what happened to an order, at which service point, with what
 * outcome (31.08.2026).
 *
 * **No money and no staff names**, same rule as {@link AdditionalWorkCardPayload}: a mechanic
 * sits in every company chat (Р17). `resultLabel` and every other Russian string here are
 * composed by the server — the portal derives no status text of its own.
 */
export interface EventCardPayload {
  kind: string;
  event: string;
  /** The headline, ready to render: «Осмотр завершён». Server-worded, like every string here. */
  title: string;
  /** Lines under the headline, in render order. The order number is the last of them. */
  lines: string[];
  /** The button's label: «Открыть заявку» / «Открыть осмотр» / «Скачать акт». */
  actionLabel: string;
  applicationId: string;
  applicationNumber: string;
  /** The service-point row, when the event happened at one. */
  applicationPtoId?: string | null;
  /** Already starts with the word «ПТО» — it is a directory value. Never prefix it. */
  ptoName?: string | null;
  /** «Осмотрен» / «Выполнен частично» / «Не выполнен» — only on `inspectionCompleted`. */
  resultLabel?: string | null;
  /** Only on `applicationCancelled`, and only when a reason was given. */
  reason?: string | null;
  action: string;
}

/**
 * Every known shape a `Card` message's payload can carry. `ClientChatMessageDto.payload`
 * stays a plain union (not a `kind`-literal discriminated one) because {@link ChatCardPayload}
 * intentionally types `kind` as `string` rather than the literal `'application'` — narrow with
 * an explicit `payload.kind === CHAT_CARD_KIND_*` check at the call site
 * (`ClientMessageCard.tsx`), not with `switch`-based structural narrowing.
 */
export type ClientCardPayload = ChatCardPayload | AdditionalWorkCardPayload | EventCardPayload;

/**
 * What kind of conversation a row of the chat list is, in the customer's own terms.
 *
 * Four values, not three: a group ТОСС takes part in (`shared`) is told apart from the company's
 * own group on purpose. Both look like «a group with a name» while scanning a list, and one of them
 * is read by us — whether that is so is the single thing a person must not have to open the roster
 * to learn.
 */
export type ClientChatKind = 'Toss' | 'Shared' | 'Group' | 'Private';

/**
 * One row of `GET /api/client/messenger/chats`.
 *
 * Carries no author name. `ClientChatMessageDto` may name its sender — decision Р17 made «add the
 * mechanic to the company chat» the sanctioned way to reach the crew, and an anonymous bubble would
 * defeat it — but that carve-out belongs to a MESSAGE, inside the conversation. A list row is
 * visible from anywhere in the portal, so its preview is the text alone.
 */
export interface ClientChatSummaryDto {
  id: string;
  /** «ТОСС» for the company's chat with us, the group's name, or the colleague's full name. */
  title: string;
  kind: ClientChatKind;
  /** Group avatar, when one was uploaded. */
  photoUrl: string | null;
  /** The last thing said. `null` before anything is, and for a chat the caller is not in. */
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  isPinned: boolean;
  isMuted: boolean;
  /**
   * Whether the caller takes part. `false` only for the chat with ТОСС, which is listed even for
   * an account the portal owner switched out of — it is how they see the conversation they were
   * taken out of, and how the «Сообщения» badge stays answerable.
   */
  isParticipant: boolean;
  /** How many people are in it. A number, never names. */
  membersCount: number;
  /** Whether this caller may rename it, set its avatar or edit its roster. Decided by the server. */
  canManage: boolean;
  /** Whether this caller may leave. False for the chat with ТОСС and for a one-to-one chat. */
  canLeave: boolean;
  /** Whether this caller may delete the chat just for themselves (`scope=me`). */
  canDeleteForMe: boolean;
  /** Whether this caller may delete the chat for every participant, permanently (`scope=everyone`). */
  canDeleteForEveryone: boolean;
}

/** Response of `GET /api/client/messenger/chats`. */
export interface ClientChatListResponse {
  items: ClientChatSummaryDto[];
  /**
   * Unread across every chat of the ACTIVE company — what the «Сообщения» badge shows. Counted
   * server-side over the unfiltered list, so a search in the list does not make the badge drop.
   */
  totalUnreadCount: number;
}

/** Body of `POST /api/client/messenger/chats`. */
export interface CreateClientChatRequest {
  /** Only `Group` and `Private` may be asked for — see `ClientChatKind`. */
  kind: Extract<ClientChatKind, 'Group' | 'Private'>;
  /** Required for a group, ignored for a one-to-one chat. */
  title?: string;
  /** Colleagues to put in it. The caller is added automatically. */
  memberIds: string[];
}

/** Answer of the group-avatar upload. */
export interface ClientChatAvatarResponse {
  photoUrl: string;
}

/** Response of `GET /api/client/messenger/chat`. There is exactly one chat (decision Р30). */
export interface ClientChatDto {
  id: string;
  /** Always the literal «ТОСС» for the client side. */
  title: string;
  /**
   * What kind of conversation this is — the list row's `kind`, on the card too, because a chat
   * deleted «for me» can be open while absent from the list. `Toss` for an empty card and for an
   * account taken out of the conversation.
   */
  kind: ClientChatKind;
  unreadCount: number;
  /** ISO-8601 UTC, `null` for a freshly created chat. */
  lastMessageAt: string | null;
  /**
   * When the ТОСС side last read this chat — the newest read cursor among its internal members,
   * `null` while nobody there has opened it.
   *
   * A timestamp of a SIDE, never a person: the portal turns the single tick on an own message
   * into a double one when `createdAt <= tossLastReadAt`. The caller's own read state is
   * excluded server-side, so opening one's own chat cannot blacken one's own ticks.
   */
  tossLastReadAt: string | null;
  /** Закреплённое сообщение переписки, или `null`. Одно на чат — как и у внутренней стороны. */
  pinnedMessage?: ClientPinnedMessageDto | null;
  /**
   * Whether the caller takes part in the conversation at all.
   *
   * `true` for a company with no chat yet — there is nothing to be excluded from, and the screen
   * must keep offering to write the first message. It turns `false` only when the chat exists and
   * the company's portal owner has switched this account out of it.
   *
   * A flag rather than a 403 because the «Сообщения» badge polls this route once a minute: an
   * error there would paint an ordinary, chosen state as a broken portal every sixty seconds.
   */
  isParticipant: boolean;
  /** Whether this caller may delete the chat just for themselves (`scope=me`). */
  canDeleteForMe: boolean;
  /** Whether this caller may delete the chat for every participant, permanently (`scope=everyone`). */
  canDeleteForEveryone: boolean;
}

/**
 * One row of `GET /api/client/messenger/chat/members`.
 *
 * The customer's own side lists EVERY portal account of the company, in the conversation or not:
 * the only roster change a customer has is flipping one of their own existing accounts, so the
 * screen is a set of switches rather than a list plus an «add» button that would need a second
 * screen to pick from two or three names.
 */
export interface ClientChatMemberDto {
  id: string;
  fullName: string;
  side: ClientChatSenderSide;
  isCurrentUser: boolean;
  /** `false` for a colleague whose access ТОСС has blocked — they stay listed, and labelled. */
  isActive: boolean;
  /** In the conversation right now. Always `true` on the ТОСС side. */
  isInChat: boolean;
  /**
   * Whether this caller may flip `isInChat` for this person. Computed on the server: false on
   * every ТОСС row, false on one's own row, and false everywhere unless the caller is the
   * company's portal owner. A switch that cannot act is not rendered at all.
   */
  canToggle: boolean;
}

/** Quoted message shown above a reply. */
export interface ClientChatReplyRefDto {
  id: string;
  senderName: string;
  /** Short excerpt of the quoted message; `null` when it had no text (a bare file). */
  preview: string | null;
  /**
   * MIME type of the quoted message's attachment — a quote of a bare photo or video reads «Фото» /
   * «Видео» instead of «Файл». `null` when the quote carries no file or has been deleted. Optional:
   * a server that predates it.
   */
  fileContentType?: string | null;
  /**
   * Stored-file id of the quoted attachment (owner decision 25.09.2026, «как в мобильном
   * приложении»): the quote draws a photo's thumbnail blob or a video's poster from it, as staff
   * and mobile do. Withheld exactly when `fileContentType` is. Optional: a server that predates it.
   */
  fileId?: string | null;
}

/**
 * One message of the company chat.
 *
 * `senderId` / `senderName` / `senderSide` are the single legitimate exception to the "no staff
 * identity" rule (r1-client-dtos §4): decision Р17 made adding a mechanic to the company chat
 * the supported way of reaching the crew.
 *
 * There is no `fileUrl` — the internal DTO's anonymous `/api/files/{id}` must not travel to the
 * portal (ADR-022). Build `/api/client/files/{fileId}` from {@link fileId} instead, via
 * `fetchClientFileBlob` in `clientFiles.api.ts`.
 */
/**
 * Одна эмодзи на сообщении, уже сгруппированная сервером.
 *
 * `reactedByMe` — ответ ДЛЯ ЧИТАЮЩЕГО: по нему решается, что делает нажатие на пилюлю — ставит
 * реакцию или снимает свою. Имена, а не идентификаторы: в кабинете идентификаторов не бывает.
 */
export interface ClientMessageReactionDto {
  emoji: string;
  count: number;
  reactedByMe: boolean;
  userNames: string[];
}

/**
 * Закреплённое сообщение переписки — то, что рисует полоса над лентой.
 *
 * Своё, а не сообщение целиком: полосе нужны имя, текст и вид, а не вложение с его подписью.
 */
export interface ClientPinnedMessageDto {
  id: string;
  senderName: string;
  content: string | null;
  messageType: ClientChatMessageType;
  fileName: string | null;
}

export interface ClientChatMessageDto {
  id: string;
  senderId: string;
  senderName: string;
  senderSide: ClientChatSenderSide;
  /** `null` only for a file-only message. */
  content: string | null;
  messageType: ClientChatMessageType;
  /**
   * Card payload as an OBJECT (contract §3.4 + §5), not a JSON string. `null` for every
   * non-card message, and also when the server failed to deserialise a malformed payload — in
   * which case the UI must degrade to rendering `content`.
   *
   * {@link ClientCardPayload} as of release Р4 — one of the KNOWN shapes, or `null`. A future
   * kind this build does not know yet (`invoice`, `document`) still arrives as a plain object
   * over the wire; it is not assignable to this type, but nothing here reads its fields without
   * checking `kind` first, so the mismatch is inert — see `ClientMessageCard.tsx`.
   */
  payload: ClientCardPayload | null;
  fileId: string | null;
  fileName: string | null;
  fileContentType: string | null;
  /**
   * Attachment size in bytes.
   *
   * NOTE: the contract's §3.4 listing calls this `fileSize`; review decision Р2 renames every
   * size field in every client DTO to `sizeBytes`, and Р2 overrides §3.4.
   */
  sizeBytes: number | null;
  /**
   * Compression state of the attachment (video looks like a photo, 25.09.2026): `None` for
   * anything but a video, `Pending`/`Processing` while the server compresses it, then `Ready` or
   * `Failed`. `null` for a message without a file. Deliberately no `posterUrl` beside it — the
   * cabinet reads its poster from `GET /api/client/files/{id}/playback`, never from an
   * `/api/files/*` address inside a message (ADR-022).
   */
  processingStatus?: ClientFileProcessingStatus | null;
  /** Video length in seconds (ffprobe), or `null` — not a video, or not processed yet. */
  durationSeconds?: number | null;
  /** Video display size after rotation, or `null` (always `null` for an image). */
  width?: number | null;
  height?: number | null;
  /**
   * Метка пачки вложений: подряд идущие сообщения одного отправителя с одним значением лента
   * рисует одним альбомом. `null` у всего, что отправлено по одному.
   */
  attachmentGroupId?: string | null;
  replyTo: ClientChatReplyRefDto | null;
  isEdited: boolean;
  isDeleted: boolean;
  /** ISO-8601 UTC. */
  createdAt: string;
  /**
   * Эмодзи-реакции, сгруппированные сервером. С сервера приходит всегда (пустым массивом, а не
   * `null`); необязательное здесь ради оптимистичного сообщения, которое собирает композер — ему
   * реагировать не на что, пока у него нет серверного id.
   */
  reactions?: ClientMessageReactionDto[];

  /**
   * LOCAL ONLY — never sent by the server, never sent to it.
   *
   * A message the composer has handed over but the server has not confirmed yet lives in the
   * cache with `_status: 'sending'` and an id of the shape `temp_*`, so the bubble appears the
   * moment Enter is pressed instead of after the round trip. A failed send keeps its bubble with
   * `_status: 'failed'` and the request that produced it, which is what the «Повторить» button
   * re-sends. Both fields are dropped as soon as the real message replaces the temporary one.
   */
  _status?: 'sending' | 'failed';

  /** LOCAL ONLY — the request to re-send when {@link _status} is `'failed'`. */
  _request?: SendClientMessageRequest;

  /**
   * LOCAL ONLY — which chat the failed send was addressed to.
   *
   * Carried on the bubble rather than read from whatever chat is open when «Повторить» is tapped:
   * a failed message can sit on screen while its author opens another conversation, and a retry
   * that resolved the chat at tap time would deliver it to the wrong one.
   */
  _chatId?: string;
}

/**
 * Response of `GET /api/client/messenger/chat/messages` — cursor paged, newest first, exactly
 * like the internal messenger. `nextCursor` is an ISO-8601 timestamp, not an opaque token.
 */
export interface ClientMessageListResponse {
  items: ClientChatMessageDto[];
  hasMore: boolean;
  nextCursor: string | null;
}

/**
 * Response of `GET /api/client/messenger/chat/messages/search` — the same bubbles the
 * conversation renders, plus the total behind the «N из M» counter.
 */
export interface ClientMessageSearchResponse {
  items: ClientChatMessageDto[];
  /** How many messages match in the whole chat, not on this page. */
  totalCount: number;
  hasMore: boolean;
  nextCursor: string | null;
}

/**
 * Response of `GET /api/client/messenger/chat/messages/{messageId}/context` — the window around
 * one message, OLDEST first (unlike every other list here), because the caller pastes it into
 * the conversation in reading order.
 */
export interface ClientMessageContextResponse {
  items: ClientChatMessageDto[];
  hasBefore: boolean;
  hasAfter: boolean;
  beforeCursor: string | null;
  afterCursor: string | null;
}

// ============================================================================
// Notifications
// ============================================================================

/** One line of the notification feed. The portal has NO push in Р1 (decision Р12, rev. 2026-08-18). */
export interface ClientNotificationDto {
  id: string;
  title: string;
  content: string;
  type: ClientNotificationType;
  isRead: boolean;
  /** Deep-link target when the notification is about an application. */
  applicationId: string | null;
  /** Deep-link target when the notification is about a chat message. */
  chatId: string | null;
  /** ISO-8601 UTC. */
  createdAt: string;
}

/** Response of `GET /api/client/notifications/unread-count`. */
export interface ClientUnreadCountDto {
  count: number;
}

// ============================================================================
// Directions (review decision Р4)
// ============================================================================

/**
 * One pickable movement direction, from `GET /api/client/directions`.
 *
 * Mirrors the internal `DirectionShortDto(Id, DirectionName)` that both `/api/lk` and
 * `/api/mobile` already return for their "all directions" lookups, and comes back as a flat
 * array — this is a dropdown source, not a paged list.
 */
export interface ClientDirectionDto {
  id: string;
  directionName: string;
}

// ============================================================================
// Documents (release Р2)
// ============================================================================

/**
 * What a document row is. `Act` is the XLSX act of one ПТО, `Report` is the PDF report of a
 * whole application, `PtoReport` is that same report narrowed to ONE visit.
 *
 * `PtoReport` joined on 31.08.2026. Until then the customer's only report was the combined
 * one, which the server hands over only once EVERY ПТО of the order has cleared ФМО — so on a
 * three-ПТО route it was one file, weeks after the first visit, while the dispatcher could
 * already read each visit on its own. The per-ПТО row is gated by that visit's status instead.
 *
 * There are no УПД / invoice rows — release Р3 (accounting) is permanently out of scope, and
 * every money key (`amount`, `price`, `paymentStatus`, `invoiceNumber`, `actNumber`) is on the
 * server's `ForbiddenKeys` blacklist.
 */
export type ClientDocumentKind = 'Act' | 'Report' | 'PtoReport';

/**
 * Why a document row is not downloadable yet.
 *
 * ONE value, on purpose. The internal reason — «не все ПТО прошли верификацию ФМО» — is
 * staff vocabulary and never leaves the server; the customer gets
 * {@link ClientDocumentDto.unavailableMessage} instead.
 *
 * **Since Р39 (26.08.2026) no LISTED row carries it** — the registry drops what is not ready
 * instead of shipping it disabled, so this type survives only for the body of the download's
 * own 409. (This comment said the opposite until 31.08.2026; the code changed and it did not.)
 */
export type ClientDocumentUnavailableReason = 'NotReadyYet';

/** File format of a document row. */
export type ClientDocumentFileFormat = 'Xlsx' | 'Pdf';

/**
 * One row of `GET /api/client/documents` — a VIRTUAL registry, not a stored artefact.
 *
 * The server projects it from `ApplicationPTOs join Applications`; nothing is persisted
 * and no file exists until the customer asks for one. Two consequences the UI must
 * respect:
 *
 *   - {@link available} is a predicate evaluated per request, with no cache. A report can
 *     go from available to unavailable and back while the screen is open — a mechanic
 *     editing an inspection resets the ПТО's payment status. So the row must re-read its
 *     own state after a failed download rather than assume the list was right.
 *   - {@link id} is a SYNTHETIC key, `act:{ptoId}` or `report:{applicationId}`. It is a
 *     React key and a download-in-flight marker, never something to send back to the API.
 *
 * Grouping rows by application is the CLIENT's job (contract §1: «группировку по заявке —
 * фронт»); the server sorts by `documentDate desc` and nothing else.
 */
export interface ClientDocumentDto {
  /** `act:{ptoId}` | `report:{applicationId}` — synthetic, see the interface comment. */
  id: string;
  kind: ClientDocumentKind;
  /** «Акт выполненных работ» | «Отчёт по заявке». Server-rendered; render as-is. */
  title: string;
  applicationId: string;
  applicationNumber: string;
  directionName: string | null;
  /** Set on an `Act` row only — a report covers the whole application. */
  ptoId: string | null;
  /** Set on an `Act` row only. */
  ptoName: string | null;
  /**
   * ISO-8601 **UTC**, rendered in the browser timezone like every other portal timestamp.
   * Acts carry the ПТО's inspection date, reports the newest one of the application.
   * `null` while neither exists.
   */
  documentDate: string | null;
  fileFormat: ClientDocumentFileFormat;
  available: boolean;
  unavailableReason: ClientDocumentUnavailableReason | null;
  /** Customer-facing sentence for the disabled row. `null` when {@link available}. */
  unavailableMessage: string | null;
  /**
   * Path of OUR API, under `ClientAccess` — never a signed or public link.
   *
   * Typed because the contract defines it, but the download clients in
   * `clientDocuments.api.ts` build the path themselves from `kind` + ids instead of
   * following this string: a URL that arrives in a payload and is then handed to
   * `apiClient` is a URL that could send the session's Authorization header somewhere
   * else. Use it for display or diagnostics, not as a request target.
   */
  downloadUrl: string;
}

/**
 * Query of `GET /api/client/documents`.
 *
 * A standalone shape — like {@link ClientApplicationFilterRequest} it deliberately does
 * NOT extend the internal `PaginatedFilterRequest`, and it has no `sortBy`: the registry
 * is always ordered by `documentDate desc`.
 */
export interface ClientDocumentFilterRequest {
  /** ≥ 1, default 1. */
  pageNumber?: number;
  /** Clamped to 1..100 server-side, default 20. */
  pageSize?: number;
  /** Restrict to one application. Another company's id answers 404. */
  applicationId?: string;
  kind?: ClientDocumentKind;
  /**
   * Free text over the four fields a registry row prints: the document title, the application
   * number, the direction and the ПТО name. Case-insensitive `Contains`, ORed, ANDed with every
   * other filter. Added 25.08.2026 — before it this registry had no search at all, while the
   * applications list had had one since Р1.
   */
  search?: string;
  directionsMovementId?: string;
  /** ISO-8601 UTC. `documentDate >= dateFrom`. */
  dateFrom?: string;
  /** ISO-8601 UTC. `documentDate < dateTo` — the upper bound is EXCLUSIVE. */
  dateTo?: string;
  /**
   * Sort field, whitelisted server-side to the registry's own columns. Default `documentDate`.
   * Added 2026-08-24 — before it the order was fixed at «newest first».
   */
  sortBy?: string;
  /** Default `desc`. */
  sortDirection?: 'asc' | 'desc';
  /**
   * Column filters from the table's header menus, as the JSON array `DataTable` emits. ANDed with
   * the named `kind` / `applicationId` / date parameters the chips and the phone write.
   */
  columnFilters?: string;
  /** The browser's IANA zone — the day the header's `documentDate` filter reads (ADR-046). */
  tz?: string;
}

// ============================================================================
// Additional-work approvals (release Р4)
//
// Contract: vault/specs/client-portal/prep-work/r4-approval-is-a-frozen-proposal-answered-in-the-chat.md
// §4-§5, overridden by r4-contract-decisions.md B3/B4/B5 — the three decisions that shape
// every type below:
//
//   - B3: the CHAT CARD (payload) carries no money and no lines — see
//     {@link AdditionalWorkCardPayload}. The lines and the total live only on
//     {@link ClientApprovalDto}, read from `GET /approvals/{id}`, a `ClientAccess`-only
//     surface the customer authenticated for.
//   - B4: `quantity` and per-line `costClient` are a DELIBERATE, SCOPED exception to the
//     "no client DTO carries a price" rule — the priced position IS the question being
//     asked. It discloses this customer's own price for a service being offered, never our
//     cost, never a rate, never another company's figure. Do not generalise the exception
//     to any other client DTO.
//   - B5: the dashboard's `pendingApprovals` is {@link ClientApprovalSummaryDto} — a
//     SEPARATE, money-free shape. `ClientDashboardDto` carries no finance widget; this
//     nudge is not one either.
// ============================================================================

/** An approval request's lifecycle. Stored as a string — see the server's `HasConversion<string>`. */
export type ClientApprovalStatus = 'Pending' | 'Approved' | 'Rejected' | 'Withdrawn';

/**
 * Why {@link ClientApprovalDto.canRespond} is `false` while the request is still `Pending`.
 * `null` once the customer HAS answered — {@link ClientApprovalDto.status} already says so.
 */
export type ClientApprovalBlockedReason = 'Decided' | 'ApplicationCancelled' | 'PtoLocked';

/** One proposed line of an approval — a snapshot taken at compose time, never a live row. */
export interface ClientApprovalLineDto {
  id: string;
  serviceName: string;
  /** «Контейнер TKRU1234567» / «ЭУ 12345 (ДГК)», or `null` for a PTO-wide (general) line. */
  targetLabel: string | null;
  quantity: number;
  /** This customer's own price for the line, or `null` when it could not be computed. */
  costClient: number | null;
}

/**
 * Response of `GET /api/client/approvals` (one row) and of `GET /approvals/{id}`,
 * `POST /approvals/{id}/approve`, `POST /approvals/{id}/reject`.
 *
 * `costClient` / `totalCostClient` are the scoped B4 exception — see the section comment
 * above. Never present, on this or any other Р4 shape: `costMechanic`, `mechanic*`,
 * `normHours`, `mileage*`, any rate, `creatorName`, `paymentStatus`, `actNumber`,
 * `serviceId`, `applicationPtoItemId`, `isAutoAdded`.
 */
export interface ClientApprovalDto {
  id: string;
  status: ClientApprovalStatus;
  /** Server-rendered Russian label for {@link status}. Render as-is; never re-map. */
  statusLabel: string;
  applicationId: string;
  applicationNumber: string;
  ptoName: string;
  /** What ТОСС wrote — why the work is needed, in the customer's words. */
  comment: string | null;
  /** ≤ 30 lines. */
  lines: ClientApprovalLineDto[];
  /** Sum of the priced lines, WITHOUT VAT; `null` when not one of them is priced. */
  totalCostClient: number | null;
  /** VAT on top of {@link totalCostClient}, per line, server-computed; `null` with it. */
  vatAmount: number | null;
  /** `totalCostClient + vatAmount`; `null` with {@link totalCostClient}. */
  totalWithVat: number | null;
  hasUnpricedLines: boolean;
  /** ISO-8601 UTC. */
  createdAt: string;
  /** `true` only while `status === 'Pending'` and nothing blocks a decision. */
  canRespond: boolean;
  blockedReason: ClientApprovalBlockedReason | null;
  /** ISO-8601 UTC, `null` while `status === 'Pending'`. */
  decidedAt: string | null;
  /**
   * `null` for `Withdrawn` — a withdrawal is done by a ТОСС employee, and staff identity
   * never reaches the portal (the same rule that blacklists `creatorName`). Set for
   * `Approved` / `Rejected`: the decider is necessarily a colleague of the customer's own
   * company.
   */
  decidedByName: string | null;
  decisionComment: string | null;
}

/**
 * One row of `GET /api/client/dashboard`'s `pendingApprovals` — release Р4, decision B5.
 *
 * Deliberately a SEPARATE, money-free shape from {@link ClientApprovalDto}: no `status` (the
 * server only ever returns `Pending` ones here), no `lines[]`, no `costClient`, no
 * `totalCostClient`. It is a nudge with a link; the customer opens the approval itself
 * (the application's «Согласования» block, or the chat card) to see the sum.
 */
export interface ClientApprovalSummaryDto {
  id: string;
  applicationId: string;
  applicationNumber: string;
  ptoName: string;
  lineCount: number;
  /** ISO-8601 UTC. */
  createdAt: string;
}

/**
 * Query of `GET /api/client/approvals`.
 *
 * A standalone shape — like {@link ClientApplicationFilterRequest} it deliberately does NOT
 * extend the internal `PaginatedFilterRequest`. Always ordered `createdAt desc`.
 */
export interface ClientApprovalFilterRequest {
  /** ≥ 1, default 1. */
  pageNumber?: number;
  /** Clamped to 1..100 server-side, default 20. */
  pageSize?: number;
  status?: ClientApprovalStatus;
  /** Restrict to one application. Another company's id answers 404. */
  applicationId?: string;
}

/** Body of `POST /api/client/approvals/{id}/approve`. */
export interface ApproveClientApprovalRequest {
  /** ≤ 1000, optional. */
  comment?: string;
}

/**
 * Body of `POST /api/client/approvals/{id}/reject`.
 *
 * `reason` is OPTIONAL, ≤ 1000 — a rejection must not be harder than an approval; forcing a
 * reason is how people stop rejecting and start not answering at all.
 */
export interface RejectClientApprovalRequest {
  reason?: string;
}

/** Response of `GET /api/client/approvals`. */
export type ClientApprovalListResponse = PaginatedResponse<ClientApprovalDto>;

/** Field limits of the approval write endpoints. */
export const CLIENT_APPROVAL_LIMITS = {
  commentMaxLength: 1000,
  reasonMaxLength: 1000,
} as const;

// ============================================================================
// Fleet: containers + service points (release Р4)
//
// Contract: vault/specs/client-portal/prep-work/r4-fleet-is-scoped-to-our-own-service-history.md
// §2-§6. Unchanged by r4-contract-decisions.md except remark 11 (query shape — backend only).
//
// The rule the whole module is named for: every fact here is a fact about work WE did for
// THIS customer, never the container's or the ПТО's own life story. A container has no owner
// column — ownership lives in `ContainerOwnershipHistory` rows — and the two internal shapes
// that look reusable (`ContainersService.GetInspectionHistoryAsync`,
// `ContainerOwnershipHistoryDto`) both leak another company's data: a mechanic's name on the
// former, every past owner's `companyName` / `companyINN` on the latter. Neither is reachable
// from any type below.
//
// No money field of any kind anywhere in this section — not even a per-ПТО or per-container
// total. A total standing next to a count is a per-visit rate one division away, which is the
// project-wide rule («ни одной поштучной ставки и ни одной величины, из которой ставка
// получается делением»). Counts and dates only.
// ============================================================================

/**
 * One row of `GET /api/client/containers` — «мой парк»: the union of containers with an open
 * ownership row for this company and containers that appeared on this company's own
 * (non-archived) orders. Neither half alone is right — see the contract §2.
 *
 * Most containers on a shipment are declared as a bare count, never itemised; itemised rows
 * are typically only the defective ones. A company shipping fifty containers a month may see
 * six rows here. That is what the data holds, not a bug in the query — the screen says so in
 * words instead of looking broken (contract §6, the empty states of `ClientContainersPage`).
 */
export interface ClientContainerListDto {
  id: string;
  containerNumber: string;
  size: string;
  type: string | null;
  /** An OPEN ownership row (`EndDate == null`) for THIS company. */
  isCurrentlyOwned: boolean;
  /** ISO-8601 UTC. Start of THIS company's open ownership period; `null` when there is none. */
  ownedSince: string | null;
  /** ISO-8601 UTC. Latest completed inspection across THIS company's orders; `null` when there is none. */
  lastServiceAt: string | null;
  /** ПТО visits of THIS company at which the container appeared. */
  serviceCount: number;
  /** What the last inspection THIS company paid for left the box in. */
  condition: ClientContainerCondition;
  /**
   * ISO-8601 UTC. Completion moment of the visit {@link condition} was read from; `null` when
   * there is none.
   *
   * Usually equal to {@link lastServiceAt} and deliberately a field of its own: a completed
   * visit that did not inspect THIS box moves the service date and leaves the verdict where it
   * was, so the two can differ by months.
   */
  conditionAt: string | null;
}

/**
 * The state of one box, as the last inspection THIS company paid for left it.
 *
 * A fact about a single container, read off one inspection row. The same four values are
 * counted over the whole fleet by {@link ClientFleetConditionSummaryDto} for the home
 * screen (release Р41) — one rule, two altitudes, and the server reads both through one
 * helper so a card and the summary above it cannot describe the same box differently.
 *
 * - `Ok` — inspected, nothing to repair.
 * - `RepairNeeded` — the last inspection asked for repairs.
 * - `InspectionFailed` — the mechanic came and could not inspect. NOT «not inspected yet».
 * - `NotInspected` — no completed visit of this company carries an inspection for the box.
 */
export type ClientContainerCondition =
  | 'NotInspected'
  | 'Ok'
  | 'RepairNeeded'
  | 'InspectionFailed';

/**
 * The container card — response of `GET /api/client/containers/{id}`.
 *
 * Deliberately carries NO ownership-history block: the internal card names every past owner
 * company, and this one may not (contract §6, owner question 7 — default «no», the customer's
 * own fleet screen is not where they learn who had the box before them).
 */
export interface ClientContainerDto {
  id: string;
  containerNumber: string;
  size: string;
  type: string | null;
  isCurrentlyOwned: boolean;
  /** ISO-8601 UTC. */
  ownedSince: string | null;
  /** How many visits {@link services} holds. */
  serviceCount: number;
  /** The same value the registry row carries — derived server-side from {@link services}. */
  condition: ClientContainerCondition;
  /** ISO-8601 UTC. Completion moment of the visit {@link condition} came from. */
  conditionAt: string | null;
  /** ISO-8601 UTC. Oldest completed visit; `null` while nothing has been closed out. */
  firstServiceAt: string | null;
  /** ISO-8601 UTC. Newest completed visit; `null` while nothing has been closed out. */
  lastServiceAt: string | null;
  /** Visits at which the box was found needing repair — per-unit verdict or bulk defect. */
  repairCount: number;
  /** Photos across every visit, inspection and repair together. */
  photoCount: number;
  /** Newest first, this company only. */
  services: ClientContainerServiceDto[];
}

/**
 * One ПТО visit at which this container was handled for the calling company.
 *
 * Never `mechanicId` / `mechanicName` (decision Р17) and never another tenant's visit on the
 * same physical box — this is NOT `ContainersService.GetInspectionHistoryAsync` (the internal
 * query filters on `ContainerId` alone and returns every customer's history on that box).
 */
export interface ClientContainerServiceDto {
  /** `ApplicationPTO` id — the list key, and the address of this visit's act. */
  applicationPtoId: string;
  applicationId: string;
  applicationNumber: string;
  /** ISO-8601 UTC. When the order was placed. */
  applicationCreatedAt: string;
  ptoName: string;
  ptoDescription: string | null;
  /** Stations covered in «в перегоне» mode. Names only — no distances, ever. */
  stations: string[];
  /** Terminal name when the work was done «на терминале»; `null` otherwise. */
  terminalName: string | null;
  status: ClientPtoStatus;
  /** Ожидает осмотра / Не выполнен / Выполнен частично / Осмотрен. Render as-is. */
  statusLabel: string;
  /** What was asked of the crew at this ПТО. */
  operationsRequest: string | null;
  /** The crew's note to the customer (`ApplicationPTO.BulkInspectionComment`). */
  crewComment: string | null;
  /**
   * An act exists for this ПТО and can be downloaded through
   * `downloadClientAct(applicationPtoId)`. The act NUMBER is never sent — it is on the
   * server's own forbidden-key list.
   */
  hasAct: boolean;
  /** ISO-8601 UTC; `null` while the visit is still open. */
  inspectionCompletedAt: string | null;
  /** Services performed on THIS box at THIS visit. Never a price — see {@link ClientPtoWorkDto}. */
  works: ClientPtoWorkDto[];
  /** `null` when this container has not been inspected per-unit on that visit. */
  inspection: ClientInspectionResultDto | null;
  /** The defect recorded «скопом» against this box, or `null`. Independent of {@link inspection}. */
  bulkRepair: ClientContainerBulkRepairDto | null;
}

/**
 * One service performed on this container at one visit.
 *
 * No money of any kind: the customer receives the same list priced, as the act, and the
 * unpriced version is what the screen may show.
 */
export interface ClientPtoWorkDto {
  serviceName: string;
  completed: boolean;
  /** The crew's note about this particular service. */
  mechanicComment: string | null;
}

/**
 * A defect recorded against this container in a ПТО's bulk repair list.
 *
 * Containers travel declared as a bare count, and a defect found during a bulk inspection is
 * appended to `ContainersNeedingRepairJson` — no ПТО item is created. Until release 28.08.2026
 * the platform, the comment and the photographs of such a defect were reachable from the act and
 * from nowhere else in the portal.
 */
export interface ClientContainerBulkRepairDto {
  platform: string | null;
  comment: string | null;
  /** File ids for `GET /api/client/files/{id}` — never raw URLs. */
  photoFileIds: string[];
  /** {@link photoFileIds} resolved to server-built refs (video release). See `ClientPtoDefectDto.photoFiles` for the nullability note. */
  photoFiles: ClientFileRefDto[] | null;
}

/**
 * One service point the calling company actually works with — element of `GET /api/client/ptos`.
 *
 * A flat, unpaged array on purpose (contract §3): a company works with a handful of service
 * points and the screen ranks them; paging a ranked list of eight rows is a worse answer than
 * sending eight rows.
 */
/**
 * Свёрнутая «Аналитика» главной — `GET /api/client/dashboard/activity` (31.08.2026).
 *
 * Отдельный запрос от дашборда, потому что панель по умолчанию закрыта и её тело не смонтировано,
 * пока её не раскрыли: считать четыре агрегата на каждый заход ради блока, который чаще всего не
 * открывают, незачем. Р23 сняла с плана тренды и прогнозы — здесь их нет, здесь окно в 30 дней.
 */
export interface ClientDashboardActivityDto {
  /** Длина окна в днях. Экран пишет «за 30 дней» из этого числа, а не из своей константы. */
  periodDays: number;
  /** Подано заявок за окно. Отменённые входят — их подавали. */
  applicationsCreated: number;
  /** Сколько контейнеров заявлено в этих заявках — сохранённый скаляр, суммой. */
  containersDeclared: number;
  /** Завершённых осмотров за окно, по одному на визит ПТО. */
  inspectionsCompleted: number;
  /** Сколько документов стало доступно для скачивания за окно (правила реестра, Р39). */
  documentsReady: number;
  /**
   * Три пункта обслуживания, с которыми компания работает больше всего, в серверном ранге.
   *
   * `null` без `client.fleet.view` — «не ваш раздел», а не «нигде не обслуживались». Тот же
   * разговор, что у `ClientDashboardDto.fleet`.
   */
  topServicePoints: ClientPtoDto[] | null;
}

export interface ClientPtoDto {
  /** `PTO.Id` — a directory id with no action attached to it; nothing in the portal accepts it as input. */
  id: string;
  name: string;
  /**
   * `PTO.Description`. Stands in for «город»: neither `PTO` nor `Station` has a city column
   * and a ПТО links stations across regions, so the matrix row «Название ПТО, город» stays
   * formally unmet — {@link stations} is the better single-hop answer to the wireframes'
   * «привычные точки».
   */
  description: string | null;
  /** Station names this ПТО operates with, alphabetically. */
  stations: string[];
  /** This company's ПТО assignments at this service point over the last 12 months. */
  applicationsCount: number;
  /** ISO-8601 UTC. Latest completed inspection here over the WHOLE history, not just the 12-month window. */
  lastServiceAt: string | null;
  /** At least one order of this company at this ПТО is live right now. */
  isActiveNow: boolean;
  /**
   * The company pinned this point (27.08.2026). Pinned points lead this list and lead the create
   * form's picker — and a pin PUTS a point on this list: it is the union of «где мы работали» and
   * «что мы закрепили», so one pinned before the first order there still appears, with a count of
   * zero and no date.
   */
  isPinned: boolean;
  /** 1-based position assigned by the server — render the order it was given, never re-derive one. */
  rank: number;
}

/**
 * Query of `GET /api/client/containers`.
 *
 * A standalone shape — like {@link ClientApprovalFilterRequest} it deliberately does NOT
 * extend the internal `PaginatedFilterRequest`, whose `ColumnFilters` / free-form `sortBy`
 * would open the internal directory's column registry, owner column included.
 */
export interface ClientContainerFilterRequest {
  /** ≥ 1, default 1. */
  pageNumber?: number;
  /** Clamped to 1..100 server-side, default 20. */
  pageSize?: number;
  /** ILIKE `%q%` on `containerNumber` only — never reaches the owner company's name. */
  search?: string;
  /** Exact match, e.g. `'40ft'`. */
  size?: string;
  /** `true` narrows to the ownership half of «мой парк» alone. Default `false` — the full union. */
  onlyOwned?: boolean;
  /** Whitelisted; anything outside it is IGNORED server-side, never rejected. */
  sortBy?:
    | 'containerNumber'
    | 'size'
    | 'type'
    | 'ownedSince'
    | 'lastServiceAt'
    | 'serviceCount';
  /** Default `asc`. */
  sortDirection?: 'asc' | 'desc';
  /**
   * Column filters from the table's header menus, as the JSON array `DataTable` emits. ANDed with
   * the named `search` / `size` / `onlyOwned` parameters the screen's own controls write.
   */
  columnFilters?: string;
  /** The browser's IANA zone — the day `ownedSince` / `lastServiceAt` filters read (ADR-046). */
  tz?: string;
}

// ============================================================================
// Header checklists: POST /api/client/{applications|documents|containers}/column-options
// ============================================================================

/**
 * Body of the portal's `column-options` routes (ADR-046). `list` is the list request exactly as
 * the page builds it (paging and sort left out); the server removes the asked column's own filter
 * — and its named twins — before narrowing, so the answer CASCADES from every other filter.
 */
export interface ClientColumnOptionsRequest<TList> {
  column: string;
  /** The browser's IANA zone; days are read in it. */
  tz?: string;
  /** Narrows a `values` answer by label. */
  search?: string;
  list?: TList;
}

/** One value a checklist may offer: `value` is the filter operand, `label` the Russian text. */
export interface ClientColumnOptionDto {
  value: string;
  label: string;
}

/** Answer of the `column-options` routes. */
export interface ClientColumnOptionsDto {
  column: string;
  kind: 'values' | 'days';
  options: ClientColumnOptionDto[];
  /** `kind: 'days'` — `YYYY-MM-DD`, ascending, days of the viewer's zone. */
  days?: string[] | null;
  /** Some row has no value — the «(Пустые)» row. */
  hasBlank: boolean;
  hasMore: boolean;
}

// ============================================================================
// Dashboard (release Р2)
// ============================================================================

/**
 * Application counters of `GET /api/client/dashboard`.
 *
 * **Five buckets, not three.** They are the five values of
 * {@link ClientApplicationStatus} — including `workDone` and `cancelled`, which an
 * earlier draft dropped, leaving two statuses counted nowhere and a set of cards whose
 * sum did not match the list. Cancelled applications ARE included; archived ones are not,
 * exactly as in `GET /api/client/applications`.
 *
 * {@link total} is the sum of the five and is sent by the server rather than added up
 * here, so the header and the cards can never disagree by a rounding of scope.
 */
export interface ClientDashboardCountersDto {
  new: number;
  inWork: number;
  workDone: number;
  closed: number;
  cancelled: number;
  total: number;
}

/**
 * Response of `GET /api/client/dashboard`.
 *
 * A company without a single application answers **200** with zeroes and three empty
 * arrays — never 404 and never 204.
 *
 * One scoping asymmetry worth knowing before reading the screen: `counters`,
 * `recentApplications` and `recentDocuments` are per **company**, while
 * `recentNotifications` is per **user** — the notification feed is keyed on the caller's
 * own id, so two colleagues signing in see the same four widgets and a different fifth.
 * The dashboard says so on screen; see `ClientDashboardPage`.
 *
 * There is no finance widget and there will not be one — release Р3 (accounting) is
 * permanently out of scope, and every money key is blacklisted server-side.
 * {@link pendingApprovals} (release Р4, decision B5) does not change that: it is the
 * money-FREE {@link ClientApprovalSummaryDto}, a nudge with a link, not a sum.
 */
export interface ClientDashboardDto {
  counters: ClientDashboardCountersDto;
  /** 3 newest by `createdAt` — three since 28.08.2026, the width of one card row. */
  recentApplications: ClientApplicationListDto[];
  /** 5 newest by `documentDate`, INCLUDING rows that are not available yet. */
  recentDocuments: ClientDocumentDto[];
  /** 3 newest — this user's own feed, not the company's. */
  recentNotifications: ClientNotificationDto[];
  /**
   * `Pending` additional-work approvals waiting on this customer, newest first — release Р4.
   * Empty array, never omitted, when there is nothing to decide.
   */
  pendingApprovals: ClientApprovalSummaryDto[];
  /**
   * «Состояние парка» — release Р41. **`null` means «not your section», not «empty»:** an
   * aggregate over the fleet IS the fleet section, so the server omits it for a caller
   * without `client.fleet.view`. A company that genuinely has nothing gets a non-null
   * block full of zeroes, and the card renders it.
   */
  fleet: ClientFleetSummaryDto | null;
}

/**
 * How many units of ONE kind — containers or energy units — are in each state the last
 * inspection this company paid for left them in.
 *
 * The four buckets are the four values of {@link ClientContainerCondition} and always sum
 * to {@link total}: the server derives `notInspected` by subtraction rather than counting
 * it, so the card can print the total beside the four without them disagreeing.
 *
 * **`total` is not the size of the customer's fleet, and for containers it is not close.**
 * Containers travel declared as a bare count and are itemised mostly when defective, so a
 * company shipping fifty a month can be counted here as six. The card says so in words
 * beside the number (`REGISTRY_SCOPE_SENTENCE`). Energy units do not have this problem —
 * they are always an entity.
 */
export interface ClientFleetConditionSummaryDto {
  total: number;
  ok: number;
  repairNeeded: number;
  inspectionFailed: number;
  notInspected: number;
}

/**
 * The two halves of the home screen's «Состояние парка» card.
 *
 * Both mean the same thing by «мой парк» — an open ownership row for this company, or the
 * unit appeared on one of its non-archived orders. The create form's equipment picker is
 * deliberately narrower (ownership only); it answers «which unit do I put on this order»,
 * which is a different question from «how many do I have».
 */
export interface ClientFleetSummaryDto {
  containers: ClientFleetConditionSummaryDto;
  equipment: ClientFleetConditionSummaryDto;
}

// ============================================================================
// Colleagues (release Р2)
// ============================================================================

/**
 * One portal account of the signed-in customer company —
 * `GET /api/client/users/colleagues`.
 *
 * **Three fields and no contacts.** The owner's Р2 decision: neither `email` nor
 * `phoneNumber`. A colleague's full name is already visible in the company chat as the
 * sender's name, so listing it discloses nothing new; their personal contact details are
 * processing of one data subject's personal data in favour of another, and the legal
 * basis for that sits in the very offer / 152-ФЗ question that is still open. Adding
 * contacts later is additive and cheap; withdrawing them after they have been shown is
 * not. Do not "complete" this DTO.
 *
 * Also absent, and not by oversight: `role` (decision Р18 — the portal never exposes
 * anyone's role), the raw `status`, `createdAt`, `lastLoginAt`, `photoUrl` and
 * `customerId`.
 *
 * These are PORTAL ACCOUNTS — people who can sign in. They are a different set from the
 * company's directory contacts in {@link ClientCompanyDto.contacts}, and somebody will be
 * in one list and not the other; the profile screen says so.
 */
export interface ClientColleagueDto {
  id: string;
  fullName: string;
  /** The server decides who "you" is — never compare ids in the UI. */
  isCurrentUser: boolean;
  /** `false` for an account whose access has been switched off. Shown, not filtered out. */
  isActive: boolean;
}

// ============================================================================
// Requests
// ============================================================================

/** One ПЛМ (platform / wagon number) line of a create request. `value` ≤ 500. */
export interface ClientApplicationPlmItemRequest {
  value: string;
}

/**
 * One energy unit put on a create request, with the wagon it travels on (release Р34).
 *
 * `plmValue` is optional and pre-filled from the unit's stored `platformNumber` — the customer
 * may know it is travelling on a different one today.
 */
export interface ClientApplicationEquipmentItemRequest {
  equipmentId: string;
  /** ≤ 500 */
  plmValue?: string;
}

/**
 * One service point the work is asked for (release Р34).
 *
 * What it deliberately cannot carry: a mechanic (Р17), the work-type flags, a terminal or a
 * station list. The customer says WHERE; who does the job and how it is classified for pricing is
 * decided on our side, before acceptance runs the calculation.
 */
export interface ClientApplicationServicePointRequest {
  ptoId: string;
  /** «Что нужно сделать», ≤ 2000 */
  operationsRequest?: string;
  /** ≤ 1000 */
  comment?: string;
}

/**
 * Body of `POST /api/client/applications`.
 *
 * EVERY field is optional and `{}` is the valid primary payload (contract §2): the customer's
 * normal flow is "заявка есть, детали уточним" and there are no required fields at all
 * (decision Р4). The server owns `customerId`, `creatorId`, `status`, `priority`, the
 * application number and every counter — none of them may be sent.
 *
 * Length and range limits are mirrored in {@link CLIENT_APPLICATION_LIMITS} for form validation.
 */
export interface CreateClientApplicationRequest {
  /** ≤ 300 */
  shipperName?: string;
  /** ≤ 300 */
  consigneeName?: string;
  /** ≤ 100, not unique */
  externalId?: string;
  /** Must exist, otherwise 404. Source: `GET /api/client/directions`. */
  directionsMovementId?: string;
  /** ≤ 500 */
  dislocation?: string;
  /** ≤ 200 */
  typeCargo?: string;
  /** ≤ 2000 */
  comment?: string;
  /** 0..10000 */
  containersTotalCount?: number;
  /** Wagon numbers with no unit behind them. Numbers that HAVE one travel on {@link equipmentItems}. */
  plmItems?: ClientApplicationPlmItemRequest[];
  /**
   * Ids from `POST /api/client/files` uploaded WITHOUT a target (the draft path). Each must
   * belong to the session's company and not yet be attached to an application.
   */
  attachedFileIds?: string[];
  /**
   * How urgent the customer considers the job (release Р34).
   *
   * A REQUEST, not a decision: the dispatcher edits it like any other field. Unset means
   * `WithoutPriority`, which is what every portal application carried before this existed.
   */
  priority?: ClientApplicationPriority;
  /**
   * Containers from the caller's own fleet (`GET /api/client/containers`). Anything else is a
   * 400 — the server re-checks active ownership, it does not trust the picker.
   */
  containerIds?: string[];
  /** Energy units from the caller's own fleet (`GET /api/client/equipment`), each with its wagon. */
  equipmentItems?: ClientApplicationEquipmentItemRequest[];
  /** Service points the work is asked for, in the customer's order (`GET /api/client/ptos/selectable`). */
  servicePoints?: ClientApplicationServicePointRequest[];
  /**
   * Where the customer already tracks this shipment (release Р34 add-on).
   *
   * At most {@link CLIENT_APPLICATION_LIMITS.trackingUrlsMax} links, each ≤
   * {@link CLIENT_APPLICATION_LIMITS.trackingUrlMaxLength} characters and `http`/`https` only —
   * the internal `PUT /api/lk/applications/{id}/tracking` constrains none of that, and those limits
   * are the client door's own. Blank entries are dropped by the server rather than refused.
   */
  trackingUrls?: string[];
}

/**
 * Body of `PUT /api/client/applications/{id}` — the customer correcting an order a dispatcher has
 * not accepted yet (owner decision, 31.08.2026).
 *
 * **A full replace, and the same field set as the create.** What the body omits is CLEARED, not
 * left alone: the form always sends every field it owns, and a «null means don't touch» rule would
 * make an emptied field indistinguishable from an untouched one. Hence the alias rather than a
 * separate shape — the server declares one write contract for both doors, and two independently
 * written interfaces here would be free to disagree with it and with each other.
 *
 * The window is `status === 'New'`: once a dispatcher accepts, the answer is 400
 * `APPLICATION_NOT_EDITABLE`, and an edit racing an acceptance answers 409 `CONCURRENCY_CONFLICT`.
 */
export type UpdateClientApplicationRequest = CreateClientApplicationRequest;

/**
 * One service point a new application may name — `GET /api/client/ptos/selectable` (release Р34).
 *
 * A different question from {@link ClientPtoDto}, which answers «ПТО, с которыми работаем» and is
 * empty for a company whose first application this is. Same omissions in both: no rates, no
 * responsible employee, no phone.
 */
export interface ClientServicePointDto {
  id: string;
  name: string;
  description: string | null;
  /** Station names, alphabetically — what tells «Москва-Сортировочная» from «Москва-Товарная». */
  stations: string[];
  /** The company pinned this point on «ПТО, с которыми работаем». */
  isPinned: boolean;
  /** This company's assignments here over the last 12 months; 0 for a point never used. */
  applicationsCount: number;
  /** ISO-8601 UTC. Latest completed inspection here for this company; `null` when there is none. */
  lastServiceAt: string | null;
}

/**
 * One energy unit of the caller's company — `GET /api/client/equipment` (release Р34).
 *
 * `typeLabel` is rendered by the server (`EquipmentType.DisplayNameRu()`), like every other portal
 * label: a second copy of that vocabulary in TypeScript is a second thing to keep in step.
 */
export interface ClientEquipmentDto {
  id: string;
  equipmentNumber: string;
  typeLabel: string;
  /** The platform the directory has this unit on; the form offers it as the default wagon number. */
  platformNumber: string | null;
}

/**
 * Body of `POST /api/client/containers` — a box the customer adds to their own fleet
 * (release Р34 add-on).
 *
 * `size` is required, unlike in the dispatcher's modal: the column is non-nullable, and the modal's
 * «Не указан» sends nothing and earns a 400 the person only sees after pressing «Создать».
 */
export interface CreateClientContainerRequest {
  /** 4–20 characters; stored upper-cased. */
  containerNumber: string;
  /** «20ft», «40ft» — free text, ≤ 10 characters, as the registry keeps it. */
  size: string;
  /** ≤ 50 characters, when the customer knows it. */
  type?: string;
}

/**
 * Body of `POST /api/client/equipment` — a unit the customer adds to their own fleet.
 *
 * `platformNumber` is asked for here although the dispatcher's modal skips it: it pre-fills «Номер
 * вагона» next to the selected unit, and without it a freshly created unit lands in the form with
 * an empty wagon field.
 */
export interface CreateClientEquipmentRequest {
  /** 3–50 characters; stored upper-cased. */
  equipmentNumber: string;
  /** `DGK` | `EGK` | `ARV` | `Genset` — the four the directory knows. */
  type: ClientEquipmentType;
  /** ≤ 50 characters. */
  platformNumber?: string;
}

/** The four energy-unit kinds, exactly as the server's `EquipmentType` spells them. */
export type ClientEquipmentType = 'DGK' | 'EGK' | 'ARV' | 'Genset';

/**
 * Human wording of {@link ClientEquipmentType} for the create modal's radio list.
 *
 * The one place the portal spells these out: everywhere else the server ships `typeLabel`. A
 * CREATE form has nothing to render from yet — the unit does not exist — so the four options carry
 * the same wording `EquipmentType.DisplayNameRu()` produces, and the wireframe's own «DGK (ДГК)»
 * pairing.
 */
export const CLIENT_EQUIPMENT_TYPE_LABELS: Record<ClientEquipmentType, string> = {
  DGK: 'ДГК',
  EGK: 'ЭГК',
  ARV: 'АРВ',
  Genset: 'Дженсет',
};

/**
 * Body of `POST /api/client/applications/{id}/cancel`.
 *
 * `reason` is REQUIRED and ≤ 500 here — deliberately stricter than the internal
 * `CancelApplicationRequest` (optional, ≤ 1000), which must not be reused.
 */
export interface CancelClientApplicationRequest {
  reason: string;
}

/**
 * Body of `POST /api/client/messenger/chat/messages`.
 *
 * There is no `messageType`: the server infers `File` when `fileId` is present and `Text`
 * otherwise. An empty `content` without a `fileId` is rejected with `EMPTY_MESSAGE`.
 */
export interface SendClientMessageRequest {
  /** ≤ 4000 */
  content?: string;
  replyToMessageId?: string;
  /** Id from `POST /api/client/files` uploaded with `chatId` as the target. */
  fileId?: string;
  fileName?: string;
  fileContentType?: string;
  /** Attachment size in bytes. Named per review decision Р2 (`sizeBytes`, never `fileSize`). */
  sizeBytes?: number;
  /** Один GUID на всю пачку вложений — по нему лента собирает альбом. */
  attachmentGroupId?: string;
}

/** Body of `POST /api/client/messenger/chat/cards`. */
export interface PostApplicationCardRequest {
  applicationId: string;
}

/**
 * Body of `POST /api/client/messenger/chats/{chatId}/messages/share-file` — «Переслать в чат» for a
 * photo or video the company already sees on an application, a ПТО or a container (owner decision
 * 30.09.2026). The server checks the file against the same sources that let the customer open it.
 */
export interface ShareClientFileRequest {
  fileId: string;
  /** Optional caption, up to 4000 characters — the message text under the file. */
  comment?: string;
}

/**
 * Query of `GET /api/client/applications`.
 *
 * A standalone shape — it deliberately does NOT extend the internal `PaginatedFilterRequest`,
 * whose `ColumnFilters`/`SortBy` would allow sorting by `mechanicNames` (contract §2).
 */
export interface ClientApplicationFilterRequest {
  /** ≥ 1, default 1. */
  pageNumber?: number;
  /** Clamped to 1..100 server-side, default 20. */
  pageSize?: number;
  /** ILIKE over applicationNumber / externalId / shipperName / consigneeName + container and equipment numbers. */
  search?: string;
  status?: ClientApplicationStatus;
  /** ISO-8601 UTC. `createdAt >= dateFrom`. */
  dateFrom?: string;
  /** ISO-8601 UTC. `createdAt < dateTo` — the upper bound is EXCLUSIVE. */
  dateTo?: string;
  directionsMovementId?: string;
  /** Default `true`; `false` hides cancelled applications. */
  includeCancelled?: boolean;
  /**
   * Whitelisted server-side; anything else is ignored. Default `createdAt`.
   *
   * Widened 2026-08-24 from three keys to every column the portal's table draws — a header that
   * offers to sort and then does not is worse than a header that offers nothing.
   */
  sortBy?:
    | 'createdAt'
    | 'applicationNumber'
    | 'status'
    | 'priority'
    | 'directionName'
    | 'containersTotalCount'
    | 'externalId';
  /** Default `desc`. */
  sortDirection?: 'asc' | 'desc';
  /**
   * Column filters that have no named parameter of their own, as the JSON array `DataTable`'s
   * header menus emit: `[{"column":"externalId","op":"contains","values":["РЖДЛ"]}]`.
   *
   * `createdAt` is the ONE column that does NOT travel here — its bounds are converted in the
   * browser's timezone and go out as the named `dateFrom` / `dateTo` (see
   * `columnFiltersToClientFilter`). `status` and `directionName` joined the blob on 02.09.2026,
   * where the server reads them as SETS; their named parameters (`status`,
   * `directionsMovementId`) still exist for the callers that build a hand-off link, and take one
   * value each. The server ANDs the named parameters with the blob, so a filter set in the
   * phone's sheet and one set in a column header narrow the same list.
   *
   * The header's day tree (ADR-046) is the exception to the exception: an `in` over day tokens
   * on `createdAt` DOES travel here, read by the server in the viewer's {@link tz}.
   */
  columnFilters?: string;
  /** The browser's IANA zone — the day every date column filter is read in (ADR-046). */
  tz?: string;
}

/** Query of the paged client endpoints that take nothing but a page. */
export interface ClientPagedRequest {
  pageNumber?: number;
  pageSize?: number;
}

/** Query of `GET /api/client/notifications`. */
export interface ClientNotificationFilterRequest extends ClientPagedRequest {
  isRead?: boolean;
}

/** Query of `GET /api/client/messenger/chat/messages`. */
export interface ClientMessagesRequest {
  /** ISO-8601 timestamp of the oldest message already loaded. Omit for the first page. */
  cursor?: string;
  /** Clamped to 1..100 server-side. */
  pageSize?: number;
}

/** Body of `PUT /api/client/messenger/chat/messages/{messageId}` — fixing one's own typo. */
export interface EditClientMessageRequest {
  /** Max 4000 characters, same ceiling as sending. */
  content: string;
}

/** Query of `GET /api/client/messenger/chat/messages/search`. */
export interface ClientMessageSearchRequest {
  /** Two characters minimum — the server rejects anything shorter. */
  query: string;
  /** ISO-8601 timestamp of the oldest hit already shown. */
  cursor?: string;
  /** Clamped to 1..100 server-side. */
  pageSize?: number;
}

// ============================================================================
// Paged envelopes
// ============================================================================

/**
 * Every paged client endpoint answers with the backend's `PaginatedList<T>`, which is exactly
 * the `PaginatedResponse<T>` this app already models (`items, totalCount, pageNumber, pageSize,
 * totalPages, hasNextPage, hasPreviousPage`). The spec's `ListResponse<T>` does not exist in
 * either codebase — contract §8 says so explicitly.
 */
export type ClientApplicationListResponse = PaginatedResponse<ClientApplicationListDto>;

/** Response of `GET /api/client/applications/{id}/history`. */
export type ClientHistoryListResponse = PaginatedResponse<ClientHistoryDto>;

/** Response of `GET /api/client/notifications`. */
export type ClientNotificationListResponse = PaginatedResponse<ClientNotificationDto>;

/** Response of `GET /api/client/documents` (release Р2). */
export type ClientDocumentListResponse = PaginatedResponse<ClientDocumentDto>;

/** Response of `GET /api/client/containers` (release Р4). */
export type ClientContainerListResponse = PaginatedResponse<ClientContainerListDto>;

// ============================================================================
// Validation limits (contract §2 — kept next to the request types they constrain)
// ============================================================================

/**
 * Field limits of the client-facing write endpoints, so the create wizard and the cancel dialog
 * validate before the round trip instead of re-deriving numbers from the spec.
 */
export const CLIENT_APPLICATION_LIMITS = {
  shipperNameMaxLength: 300,
  consigneeNameMaxLength: 300,
  externalIdMaxLength: 100,
  dislocationMaxLength: 500,
  typeCargoMaxLength: 2000,
  commentMaxLength: 2000,
  plmValueMaxLength: 500,
  containersTotalCountMin: 0,
  containersTotalCountMax: 10000,
  cancelReasonMaxLength: 500,
  /** Release Р34 add-on — mirrors `CreateClientApplicationRequest.MaxTrackingUrls`. */
  trackingUrlsMax: 10,
  trackingUrlMaxLength: 500,
  /** `POST /api/client/containers` — the create-modal half of the picker. */
  containerNumberMinLength: 4,
  containerNumberMaxLength: 20,
  containerSizeMaxLength: 10,
  containerTypeMaxLength: 50,
  /** `POST /api/client/equipment`. */
  equipmentNumberMinLength: 3,
  equipmentNumberMaxLength: 50,
  platformNumberMaxLength: 50,
} as const;

/** Field limits of `POST /api/client/messenger/chat/messages`. */
export const CLIENT_MESSAGE_LIMITS = {
  contentMaxLength: 4000,
} as const;

/**
 * Limits of the template endpoints that are NOT already in {@link CLIENT_APPLICATION_LIMITS}.
 *
 * The nine prefill fields deliberately reuse that object's numbers rather than repeating
 * them here — r5-application-templates.md §2 states every length "mirrors the create request
 * it feeds", so a second copy of the same nine numbers would be one more place for the two
 * to drift apart. Only what is genuinely new to templates lives here: the name field (the
 * create request has none), the wagon-count cap (the create request has none — see the Р1
 * follow-up noted in r5-contract-decisions.md §6) and the per-company template cap.
 */
export const CLIENT_APPLICATION_TEMPLATE_LIMITS = {
  nameMaxLength: 200,
  plmValuesMaxCount: 100,
  maxTemplatesPerCompany: 50,
} as const;

// ============================================================================
// Duplicate hint: POST /api/client/applications/duplicate-check
// ============================================================================

/**
 * The picked keys of the create / edit form (spec application-merge/a-unit-already-in-an-open-
 * application-is-hinted-and-never-blocked, §4). No company — the server reads the session's active
 * one. Each list holds at most 500 values; empty lists answer `[]`.
 */
export interface ClientDuplicateCheckRequest {
  /** The application being edited — never matches itself; a foreign id is ignored. */
  excludeApplicationId?: string;
  equipmentIds?: string[];
  containerIds?: string[];
  plmValues?: string[];
}

/**
 * One open application of the company sharing a key (`New` or `InWork` only). The values fill
 * `equipment`, then `containers`, then `plms`, up to 20 in all; `moreCount` is one total across
 * the three lists.
 */
export interface ClientDuplicateMatchDto {
  applicationId: string;
  applicationNumber: string;
  status: ClientApplicationStatus;
  statusLabel: string;
  /** UTC. */
  createdAt: string;
  equipment: string[];
  containers: string[];
  plms: string[];
  moreCount: number;
}
